import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from "react-router-dom";
import mapboxgl from 'mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import mapview from '../../../src/assets/marker.png'
import { mapboxToken } from '../../configuration';

mapboxgl.accessToken = mapboxToken;

const DevicesMapView = ({ onPopupLinkClick, deviceList }) => {
  const mapContainerRef = useRef(null);
  const [map, setMap] = useState(null);
  const [popupInfo, setPopupInfo] = useState(null);
  const [compInfo, setCompInfo] = useState(null);
  const [visible, setVisible] = useState("true");
  const navigate = useNavigate();

  const getStatusColorExpression = (property = 'status') => [
    'case',
    ['==', ['get', property], 'ERROR'], '#ff8027',
    ['==', ['get', property], 'WARNING'], '#fed60e',
    ['==', ['get', property], 'NOTIFICATION'], '#006FC1',
    ['==', ['get', property], 'COMPLETED'], '#AAD8F9',
    '#00B845'
  ];

  useEffect(() => {
    setCompInfo(deviceList);
  }, [deviceList]);

  useEffect(() => {
    if (!compInfo) return;
    if (!mapboxgl.supported()) {
      console.error("WebGL is not supported in this browser. Map cannot be initialized.");
      return;
    }
    let map;
    try {
      map = new mapboxgl.Map({
        container: mapContainerRef.current,
        style: 'mapbox://styles/mapbox/streets-v11',
        center: [-122.4194, 37.7749],
        zoom: 2.5
      });
    } catch (e) {
      console.error("Failed to initialize map:", e);
      return;
    }
    map.setProjection('globe');
    map.addControl(new mapboxgl.NavigationControl());
    map.addControl(new mapboxgl.FullscreenControl());

    const layers = [
      { id: 'outdoors-v11', name: 'Terrain' },
      { id: 'satellite-v9', name: 'Satellite' },
    ];

    const layerMenu = document.createElement('div');
    layerMenu.className = 'map-container mapboxgl-ctrl-group mapboxgl-ctrl horizontal-buttons';

    layers.forEach(layer => {
      const button = document.createElement('button');
      button.textContent = layer.name;
      button.className = layer.id === 'streets-v11' ? 'active' : '';
      button.onclick = () => {
        map.setStyle('mapbox://styles/mapbox/' + layer.id);
        document.querySelectorAll('.horizontal-buttons button').forEach(btn => btn.classList.remove('active'));
        button.classList.add('active');
      };
      layerMenu.appendChild(button);
    });

    map.addControl({
      onAdd: function () {
        return layerMenu;
      },
      onRemove: function () {
        layerMenu.parentNode.removeChild(layerMenu);
      }
    });
    const normalizeLngLat = ([lng, lat]) => {
      lng = ((lng + 180) % 360 + 360) % 360 - 180;
      lat = Math.max(-90, Math.min(90, lat));
      return [lng, lat];
    };

    const jitter = (lon, lat, offset) => {
      const randomAngle = Math.random() * 2 * Math.PI;
      const randomRadius = offset * Math.sqrt(Math.random());
      const deltaLon = randomRadius * Math.cos(randomAngle);
      const deltaLat = randomRadius * Math.sin(randomAngle);
      return normalizeLngLat([lon + deltaLon, lat + deltaLat]);
    };
    const offsetValue = 0.0003;

    const addMapLayers = () => {
      if (!map.getSource('clusters')) {
        map.addSource('clusters', {
          type: 'geojson',
          data: {
            type: 'FeatureCollection',
            features: compInfo
              ?.filter(point =>
                typeof point?.fields?.location?.longitude === 'number' &&
                typeof point?.fields?.location?.latitude === 'number' &&
                !isNaN(point.fields.location.longitude) &&
                !isNaN(point.fields.location.latitude)
              )?.map(point => (point !== undefined ? {
              type: 'Feature',
              properties: {
                id: 1,
                value: point?.id,
                uid: point?.uid,
                title: point?.name,
                status: point?.customdata?.healthStatus 
              },
              geometry: {
                type: 'Point',
                coordinates: jitter(point?.fields?.location?.longitude, point?.fields?.location?.latitude, offsetValue)
              }
            } : ""))
          },
          cluster: true,
          clusterMaxZoom: 14,
          clusterRadius: 50,
           clusterProperties: {
            error_count: ['+', ['case', ['==', ['get', 'status'], 'ERROR'], 1, 0]],
            warning_count: ['+', ['case', ['==', ['get', 'status'], 'WARNING'], 1, 0]],
            notification_count: ['+', ['case', ['==', ['get', 'status'], 'NOTIFICATION'], 1, 0]],
            completed_count: ['+', ['case', ['==', ['get', 'status'], 'COMPLETED'], 1, 0]],
          }
        });
        map.addLayer({
          id: 'clusters',
          type: 'circle',
          source: 'clusters',
          filter: ['has', 'point_count'],
          paint: {
            'circle-color': [
              'case',
              ['>', ['get', 'error_count'], ['max', ['get', 'warning_count'], ['get', 'notification_count'], ['get', 'completed_count']]], '#ff8027',
              ['>', ['get', 'warning_count'], ['max', ['get', 'error_count'], ['get', 'notification_count'], ['get', 'completed_count']]], '#fed60e',
              ['>', ['get', 'notification_count'], ['max', ['get', 'error_count'], ['get', 'warning_count'], ['get', 'completed_count']]], '#006FC1',
              ['>', ['get', 'completed_count'], ['max', ['get', 'error_count'], ['get', 'warning_count'], ['get', 'notification_count']]], '#AAD8F9',
              '#00B845'
            ],
            'circle-radius': 20,
            'circle-stroke-width': 1,
            'circle-stroke-color': '#fff'
          }
        });

        map.addLayer({
          id: 'cluster-count',
          type: 'symbol',
          source: 'clusters',
          filter: ['has', 'point_count'],
          layout: {
            'text-field': '{point_count_abbreviated}',
            'text-font': ['DIN Offc Pro Medium', 'Arial Unicode MS Bold'],
            'text-size': 12,
            'text-offset': [0, 0]
          }
        });
        map.addLayer({
          id: 'unclustered-point',
          type: 'circle',
          source: 'clusters',
          filter: ['!', ['has', 'point_count']],
          paint: {
            'circle-color': getStatusColorExpression('status'),
            'circle-radius': 15,
            'circle-stroke-width': 1,
            'circle-stroke-color': '#fff'
          }
        });

        map.addLayer({
          id: 'unclustered-point-label',
          type: 'symbol',
          source: 'clusters',
          filter: ['!', ['has', 'point_count']],
          layout: {
            'text-field': '{id}',
            'text-font': ['DIN Offc Pro Medium', 'Arial Unicode MS Bold'],
            'text-size': 12,
          },
          paint: {
            'text-color': '#fff'
          }
        });

        let popup = new mapboxgl.Popup({
          closeButton: false,
          closeOnClick: false,
          className: 'custom-popup'
        });

        map.on('click', 'clusters', e => {
          const features = map.queryRenderedFeatures(e.point, {
            layers: ['clusters']
          });
          const clusterId = features[0].properties.cluster_id;
          map.getSource('clusters').getClusterExpansionZoom(clusterId, (err, zoom) => {
            if (err) return;
            const [longitude, latitude] = features[0].geometry.coordinates;
            map.easeTo({
              center: [longitude, latitude],
              zoom: zoom,
              padding: { top: 50, bottom: 50, left: 50, right: 50 }
            });
          });
        });

        map.on('click', 'unclustered-point', e => {
          const coordinates = e.features[0].geometry.coordinates.slice();
          const { title, id, orgName, value, uid } = e.features[0].properties;
          setPopupInfo({ title: title, coordinates: coordinates });
          const popupContent = document.createElement('div');
          popupContent.innerHTML = `
            <div>
              <a class="mappopupheading" id="location" data-action="${title}" href="#">${title}</a>
              <div class="mappopup-row">
                Actions: <li class="mappopuplink" id="viewdeviceLink">View device</li>
              </div>
            </div>
          `;
          popupContent.addEventListener('click', function (event) {
            event.preventDefault();
            const targetId = event.target.id;
            if (targetId === 'location' || targetId === 'viewdeviceLink') {
              navigate(`/devices/${uid}/map`);
            }
          });
          new mapboxgl.Popup()
            .setLngLat(coordinates)
            .setDOMContent(popupContent)
            .addTo(map);
        });

        map.on('mouseenter', 'unclustered-point', (e) => {
          map.getCanvas().style.cursor = 'pointer';
          const coordinates = e.features[0].geometry.coordinates.slice();
          const { title } = e.features[0].properties;
          popup
            .setLngLat(coordinates)
            .setHTML(`<p class="mappopup-info">${title}</p>`)
            .addTo(map);
        });

        map.on('mouseleave', 'unclustered-point', () => {
          map.getCanvas().style.cursor = '';
          popup.remove();
        });

        map.on('mouseenter', 'clusters', () => {
          map.getCanvas().style.cursor = 'pointer';
        });
        map.on('mouseleave', 'clusters', () => {
          map.getCanvas().style.cursor = '';
        });
      }
    };
    map.on('styledata', addMapLayers);
    map.on('load', addMapLayers);
    setMap(map);
    return () => map.remove();
  }, [compInfo, onPopupLinkClick, visible]);

  useEffect(() => {
    const handleClick = () => {
      setVisible("false");
      setTimeout(() => setVisible("true"), 1);
    };
    const elements = document.getElementsByClassName('nev-menu');
    for (let element of elements) {
      element.addEventListener('click', handleClick);
    }
    return () => {
      for (let element of elements) {
        element.removeEventListener('click', handleClick);
      }
    };
  }, []);

  return (
    <div>
      <div className="map-container" ref={mapContainerRef} style={{
        height: '35vh',
        width: '100%',
      }}
      />
    </div>
  );
};
export default DevicesMapView;